import type { DashboardField, DashboardWidget, DashboardWidgetType } from './types';
import { wellsOf } from './widgetTypes';
import type { DashboardWell } from './widgetTypes';

/**
 * What changing a widget's visualisation does to the fields already placed
 * (SL-1665, AC3).
 *
 * Pure and read-only: the panel calls it **before** `setWidgetType` to decide
 * whether the switch needs a confirmation, and to tell the user what it costs.
 * It describes; `setWidgetType` does. Both agree because both are built on the
 * same rule — a **role** is a well's `accepts`, `any` included — and on the same
 * parking slot (`parkedSlot`). They are not merely alike: both call
 * `applyTypeChange`, and the plan is what that function reports.
 *
 * `wells` holds only what the widget's type declares. Whatever the new type does
 * not read under the same key **and** role is parked, so it is never inert in
 * `wells` waiting to be misread by another type.
 */

/**
 * Why a configured well does not carry over to the new type. All three are
 * parked, not lost: they come back if the type does, until the dashboard is saved.
 */
export type DiscardReason = 'noWell' | 'roleMismatch' | 'overflow';

/** One well's worth of fields, named for the panel to list. */
export interface WellMove {
  wellKey: string;
  /** The i18n leaf that names the well — `labelKey ?? key` — in the type it is described in. */
  labelKey: string;
  fieldCount: number;
}

export interface DiscardedWell extends WellMove {
  reason: DiscardReason;
}

export interface TypeChangePlan {
  /** Wells whose fields the new type reads as they are. */
  kept: WellMove[];
  /** Wells, or the part of one, the new type will not read. */
  discarded: DiscardedWell[];
  /** Wells parked by an earlier switch that the new type takes back. */
  restored: WellMove[];
  /** True when something is discarded: the only case worth interrupting for. */
  needsConfirm: boolean;
}

/** The `parkedWells` key of a well's fields under one role. */
export const parkedSlot = (wellKey: string, accepts: DashboardWell['accepts']): string =>
  `${wellKey}:${accepts}`;

/** What `applyTypeChange` produced: the plan's lists plus the resulting fields. */
export interface TypeChangeResult extends Omit<TypeChangePlan, 'needsConfirm'> {
  wells: DashboardWidget['wells'];
  /** The widget's `parkedWells` after the switch; may be empty. */
  parked: NonNullable<DashboardWidget['parkedWells']>;
}

const labelOf = (well: DashboardWell): string => well.labelKey ?? well.key;

/**
 * Parks `fields` under `slot` without overwriting what an earlier switch already
 * parked there: the two lists are merged and deduped by `columnId`, the fields
 * being parked **now** first. They are the ones that were in the well one hop
 * ago, so they sit before what was parked earlier — Bar [A, B, C] → Scatter
 * (parks [B, C]) → KPI (parks [A]) leaves [A, B, C], and going back to Bar
 * restores the original order.
 */
const park = (parked: TypeChangeResult['parked'], slot: string, fields: DashboardField[]): void => {
  const seen = new Set<string>();
  parked[slot] = [...fields, ...(parked[slot] ?? [])].filter((field) => {
    if (seen.has(field.columnId)) return false;
    seen.add(field.columnId);
    return true;
  });
};

/**
 * Applies the switch from the widget's current type to `nextType` and reports
 * what it did. The single implementation behind `setWidgetType` and
 * `planTypeChange`.
 *
 * - A well the new type reads under the same key and role is **kept**.
 * - Every field of any other well — the key is undeclared (`noWell`) or read
 *   under another role (`roleMismatch`) — is **parked** under
 *   `parkedSlot(key, previousRole)`. A slot an earlier switch already filled is
 *   merged into, never replaced (see `park`). Bar → KPI → Scatter therefore does not leave
 *   bar's dimension in `wells.xAxis` for the scatter to read as a measure.
 * - A single-valued well that receives several fields keeps the first; the
 *   rest (`overflow`) are parked under the well's own slot, so going back to a
 *   multiple well (Table → Matrix → Table) returns them.
 * - A parked slot the new type declares is **restored** into an empty well, so
 *   it never overwrites what the user has put there since. The one exception is
 *   the overflow return: into a multiple well that already holds fields, the
 *   parked ones not yet present are appended.
 *
 * An untyped widget, or the type it already has, changes nothing.
 */
export function applyTypeChange(
  widget: DashboardWidget,
  nextType: DashboardWidgetType,
): TypeChangeResult {
  const kept: WellMove[] = [];
  const discarded: DiscardedWell[] = [];
  const restored: WellMove[] = [];
  const wells = { ...widget.wells };
  const parked = { ...widget.parkedWells };

  if (!widget.type || widget.type === nextType) {
    return { wells, parked, kept, discarded, restored };
  }

  const nextWells = wellsOf(nextType);

  for (const previous of wellsOf(widget.type)) {
    const fields = wells[previous.key];
    if (!fields?.length) continue;

    const move = { wellKey: previous.key, labelKey: labelOf(previous), fieldCount: fields.length };
    const next = nextWells.find((candidate) => candidate.key === previous.key);
    const slot = parkedSlot(previous.key, previous.accepts);

    if (!next || next.accepts !== previous.accepts) {
      discarded.push({ ...move, reason: next ? 'roleMismatch' : 'noWell' });
      park(parked, slot, fields);
      delete wells[previous.key];
    } else if (!next.multiple && fields.length > 1) {
      kept.push({ ...move, fieldCount: 1 });
      discarded.push({ ...move, fieldCount: fields.length - 1, reason: 'overflow' });
      wells[previous.key] = fields.slice(0, 1);
      park(parked, slot, fields.slice(1));
    } else {
      kept.push(move);
    }
  }

  for (const next of nextWells) {
    const slot = parkedSlot(next.key, next.accepts);
    const back = parked[slot];
    if (!back?.length) continue;

    const current = wells[next.key] ?? [];
    let returning = back;
    if (current.length > 0) {
      if (!next.multiple) continue;
      const present = new Set(current.map((field) => field.columnId));
      returning = back.filter((field) => !present.has(field.columnId));
    }

    delete parked[slot];
    if (returning.length === 0) continue;

    wells[next.key] = [...current, ...returning];
    restored.push({ wellKey: next.key, labelKey: labelOf(next), fieldCount: returning.length });
  }

  return { wells, parked, kept, discarded, restored };
}

/**
 * Describes the switch to `nextType` without doing it: what `applyTypeChange`
 * would keep, park and bring back.
 */
export function planTypeChange(
  widget: DashboardWidget,
  nextType: DashboardWidgetType,
): TypeChangePlan {
  const { kept, discarded, restored } = applyTypeChange(widget, nextType);
  return { kept, discarded, restored, needsConfirm: discarded.length > 0 };
}
